import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { BrainCircuit, CheckCircle2, Circle, CreditCard, Download, Mail, Phone, Workflow } from 'lucide-react';
import type {
  AdminLocalProcessorSettingsResponse,
  AdminNumbersResponse,
  AdminStaffProfileSetupCompletenessResponse,
  StaffGoogleWorkspaceAuthorizationStartResponse,
  ServerBillingStatus,
} from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { engineLocalAiSetupLink, engineStartServiceLink } from '../lib/oracord-engine-setup-command';
import { API_BASE_URL } from '../lib/config';
import { authClient } from '../lib/auth-client';
import { useSessionStore } from '../stores/session';
import { describeLocalAiSetup, engineSetupCheckDone, engineSetupStatusItems } from '../lib/engine-setup-checks';
import { describeEngineSetupFlow } from '../lib/engine-setup-flow';
import { describeEngineDownload } from '../lib/engine-download';
import {
  describeEngineHandoffStatus,
  type SetupSourceStatus,
} from '../lib/setup-source-status';
import { PageHeader, SectionCard } from '../components/WebPresentation';
import { AdminActionDialog } from '../components/AdminActionDialog';

export default function Setup() {
  const [billing, setBilling] = useState<ServerBillingStatus | null>(null);
  const [engine, setEngine] = useState<AdminLocalProcessorSettingsResponse | null>(null);
  const [staffSetup, setStaffSetup] = useState<AdminStaffProfileSetupCompletenessResponse | null>(null);
  const [businessLines, setBusinessLines] = useState<AdminNumbersResponse | null>(null);
  const { data: sessionData } = authClient.useSession();
  const activeOrganizationId = useSessionStore(state => state.activeOrganizationId);
  const [openedSetupLink, setOpenedSetupLink] = useState(false);
  const [installerDownloaded, setInstallerDownloaded] = useState(false);
  const [installerAcknowledged, setInstallerAcknowledged] = useState(false);
  const [localAiSetupLaunched, setLocalAiSetupLaunched] = useState(false);
  const [googleAuthorizationStarting, setGoogleAuthorizationStarting] = useState(false);
  const [googleAuthorizationError, setGoogleAuthorizationError] = useState<string | null>(null);
  const [engineLaunchDialog, setEngineLaunchDialog] = useState<{
    href: string;
    kind: 'pair' | 'start' | 'local-ai';
  } | null>(null);

  const load = useCallback(async () => {
    const [billingResult, engineResult, staffSetupResult, businessLinesResult] = await Promise.allSettled([
      apiFetch<ServerBillingStatus>('/api/billing/status'),
      apiFetch<AdminLocalProcessorSettingsResponse>('/api/admin/settings/local-processor'),
      apiFetch<AdminStaffProfileSetupCompletenessResponse>('/api/admin/settings/staff-profile-setup-completeness'),
      apiFetch<AdminNumbersResponse>('/api/admin/numbers'),
    ]);
    if (billingResult.status === 'fulfilled') setBilling(billingResult.value);
    if (engineResult.status === 'fulfilled') setEngine(engineResult.value);
    if (staffSetupResult.status === 'fulfilled') setStaffSetup(staffSetupResult.value);
    if (businessLinesResult.status === 'fulfilled') setBusinessLines(businessLinesResult.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const engineReady = describeEngineHandoffStatus({ openedSetupLink, engine }).state === 'ready';
    const localAiReady = Boolean(engine?.engine.localAi?.smokePassed);
    const waitingForEngine = openedSetupLink && !engineReady;
    const waitingForLocalAi = localAiSetupLaunched && !localAiReady;
    const intervalMs = waitingForEngine || waitingForLocalAi ? 3000 : 15000;
    const interval = window.setInterval(() => {
      void load();
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [engine, load, localAiSetupLaunched, openedSetupLink]);

  const storageScope = engine?.organizationId ?? sessionData?.user?.id ?? null;

  useEffect(() => {
    if (!storageScope) {
      setInstallerDownloaded(false);
      setInstallerAcknowledged(false);
      return;
    }
    const downloaded = window.localStorage.getItem(installerStorageKey(storageScope, 'downloaded')) === 'true';
    const acknowledged = window.localStorage.getItem(installerStorageKey(storageScope, 'installed-acknowledged')) === 'true';
    setInstallerDownloaded(downloaded || acknowledged);
    setInstallerAcknowledged(acknowledged);
  }, [storageScope]);

  const recordInstallerDownloaded = useCallback(() => {
    if (storageScope) {
      window.localStorage.setItem(installerStorageKey(storageScope, 'downloaded'), 'true');
    }
    setInstallerDownloaded(true);
  }, [storageScope]);

  const acknowledgeInstaller = useCallback(() => {
    if (storageScope) {
      window.localStorage.setItem(installerStorageKey(storageScope, 'downloaded'), 'true');
      window.localStorage.setItem(installerStorageKey(storageScope, 'installed-acknowledged'), 'true');
    }
    setInstallerDownloaded(true);
    setInstallerAcknowledged(true);
  }, [storageScope]);

  const engineDownload = describeEngineDownload(engine?.engine);
  const pairingLaunchLink = activeOrganizationId ? buildOracordEnginePairingLaunchLink(activeOrganizationId) : '#';
  const engineStartLink = engineStartServiceLink();
  const localAiSetupLink = engineLocalAiSetupLink();
  const engineInstalled = engineSetupCheckDone(engine?.engine, 'Engine installed');
  const businessSetupLoaded = engineSetupCheckDone(engine?.engine, 'Business setup loaded');
  const engineRunning = engineSetupCheckDone(engine?.engine, 'Running');
  const billingActive = Boolean(billing?.active);
  const activePlan = billing?.plan ?? null;
  const soloMembership = billingActive && isSoloPlan(activePlan);
  const engineHandoffStatus = describeEngineHandoffStatus({ openedSetupLink, engine });
  const engineHandoffReady = engineHandoffStatus.state === 'ready';
  const engineSetupFlow = describeEngineSetupFlow({
    engineInstalled,
    installerDownloaded,
    installerAcknowledged,
    billingActive,
    activeOrganizationId,
    engineHandoffReady,
  });
  const installerAcknowledgedOnly = engineSetupFlow.installerAcknowledgedOnly;
  const effectiveInstallerAcknowledged = engineSetupFlow.effectiveInstallerAcknowledged;
  const engineCoreSetupComplete = engineInstalled && engineHandoffReady;
  const localAiSetup = describeLocalAiSetup(engine?.engine.localAi);
  const localAiReady = localAiSetup.done;
  const showEngineInstallCard = billingActive && !engineInstalled;
  const showLocalAiSetupCard = billingActive && engineRunning && !localAiReady;
  const engineStatusItems = engineSetupStatusItems(engine?.engine);
  const visibleEngineStatusItems = engineStatusItems.filter(item => item.mark !== 'Business setup loaded');
  const showEngineRepairCard = billingActive && (
    engineSetupFlow.installerAcknowledgedOnly ||
    (openedSetupLink && !engineHandoffReady) ||
    (engineInstalled && engineHandoffReady && !engineRunning) ||
    (localAiSetupLaunched && !localAiReady)
  );

  const openPairingSetupLink = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setEngineLaunchDialog({ href: pairingLaunchLink, kind: 'pair' });
  }, [pairingLaunchLink]);

  const openEngineStartLink = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setEngineLaunchDialog({ href: engineStartLink, kind: 'start' });
  }, [engineStartLink]);

  const openLocalAiSetupLink = useCallback(() => {
    setLocalAiSetupLaunched(true);
    setEngineLaunchDialog({ href: localAiSetupLink, kind: 'local-ai' });
  }, [localAiSetupLink]);

  const checkStatus = useCallback(() => {
    void load();
  }, [load]);

  const confirmEngineLaunch = useCallback(() => {
    if (!engineLaunchDialog) return;
    const { href, kind } = engineLaunchDialog;
    setEngineLaunchDialog(null);
    if (kind === 'pair') {
      setOpenedSetupLink(true);
      window.setTimeout(() => void load(), 1500);
    } else if (kind === 'local-ai') {
      setLocalAiSetupLaunched(true);
      window.setTimeout(() => void load(), 3000);
      window.setTimeout(() => void load(), 9000);
    } else {
      window.setTimeout(() => void load(), 3000);
    }
    window.location.href = href;
  }, [engineLaunchDialog, load]);

  const startGoogleAuthorization = useCallback(async () => {
    setGoogleAuthorizationStarting(true);
    setGoogleAuthorizationError(null);
    try {
      const response = await apiFetch<StaffGoogleWorkspaceAuthorizationStartResponse>('/api/auth/google-workspace/authorization/start', {
        method: 'POST',
      });
      if (response.authorizationUrl && response.redirectAvailable) {
        window.location.assign(response.authorizationUrl);
        return;
      }
      setGoogleAuthorizationError('Google authorization is not ready yet. Refresh Setup and try again.');
    } catch (err) {
      setGoogleAuthorizationError(err instanceof ApiError && err.message
        ? err.message
        : 'Could not start Google authorization. Refresh Setup and try again.');
    } finally {
      setGoogleAuthorizationStarting(false);
    }
  }, []);

  const activeProfileCount = staffSetup?.summary.activeProfiles ?? 0;
  const googleWorkspaceNeedsSetup = staffSetup?.summary.needsGoogleWorkspace ?? 0;
  const googleWorkspaceConfigured = staffSetup?.googleWorkspace.configured ?? false;
  const soloGoogleConnected = soloMembership && googleWorkspaceConfigured && activeProfileCount > 0 && googleWorkspaceNeedsSetup === 0;
  const businessLineCount = businessLines?.rows.length ?? 0;
  const businessLineConnected = businessLineCount > 0;
  const signedInEmail = sessionData?.user?.email ?? null;
  const signedInDomain = emailDomain(signedInEmail);
  const signedInWithPersonalGoogle = signedInDomain === 'gmail.com' || signedInDomain === 'googlemail.com';
  const showGoogleAccountMismatchNote = !soloMembership && signedInWithPersonalGoogle && Boolean(signedInEmail);
  const steps = [
    {
      label: 'Choose a plan',
      detail: billingActive ? `${formatPlan(billing?.plan ?? 'Plan')} is active.` : 'Pick Solo or Team to unlock setup.',
      done: billingActive,
      icon: CreditCard,
      to: '/billing',
      action: billingActive ? 'Plan active' : 'Choose plan',
      completedTone: billingActive,
      statusOnly: billingActive,
    },
    ...(!engineCoreSetupComplete
      ? [
          {
            label: 'Install Oracord Engine',
            detail: engineSetupFlow.installDetail || `${engineDownload.detail} ${engineDownload.platformNote}`,
            done: engineInstalled,
            icon: Download,
            to: engineDownload.path || '/settings',
            action: engineSetupFlow.installAction || (engineDownload.path ? engineDownload.action : 'Open install help'),
            externalDownload: !installerDownloaded && engineDownload.externalDownload,
            buttonAction: !engineInstalled && installerDownloaded && !installerAcknowledgedOnly ? acknowledgeInstaller : undefined,
            onClick: !installerDownloaded && engineDownload.externalDownload ? recordInstallerDownloaded : undefined,
            completedTone: engineInstalled,
            statusOnly: engineSetupFlow.installStatusOnly,
          },
        ]
      : []),
    ...(!engineCoreSetupComplete
      ? [
          {
            label: 'Pair the Engine',
            detail: engineSetupFlow.pairDetail,
            done: engineHandoffReady,
            icon: Workflow,
            action: engineSetupFlow.pairAction,
            sourceStatus: engineHandoffStatus,
            to: pairingLaunchLink,
            externalAction: true,
            onClick: openPairingSetupLink,
            disabled: engineSetupFlow.pairDisabled,
          },
        ]
      : []),
    {
      label: 'Start Engine',
      detail: engineRunning
        ? 'Engine is running on this Mac.'
        : !engineHandoffReady
          ? 'Pair this Mac before starting Engine.'
          : 'Start the local Engine service on this Mac.',
      done: engineRunning,
      icon: Workflow,
      to: engineRunning || !engineHandoffReady ? '/settings?tab=oracord' : engineStartLink,
      action: engineRunning ? 'Running' : !engineHandoffReady ? 'Pair first' : 'Start Engine',
      disabled: !engineRunning && !engineHandoffReady,
      externalAction: !engineRunning && engineHandoffReady,
      onClick: !engineRunning && engineHandoffReady ? openEngineStartLink : undefined,
      completedTone: engineRunning,
      statusOnly: engineRunning,
    },
    {
      label: localAiSetup.label,
      detail: localAiSetup.detail,
      done: localAiSetup.done,
      icon: BrainCircuit,
      to: localAiSetup.route,
      action: localAiSetup.action,
      buttonAction: !localAiSetup.done ? openLocalAiSetupLink : undefined,
      disabled: !engineRunning || localAiSetup.done,
      completedTone: localAiSetup.done,
      statusOnly: localAiSetup.done,
    },

    {
      label: soloMembership ? 'Connect Gmail and Calendar' : 'Add team members',
      detail: soloMembership
        ? soloGoogleConnected
          ? 'Google is connected for this Solo workspace.'
          : googleWorkspaceConfigured
            ? 'Connect the Google account you want Oracord to use.'
            : 'Google connection is not ready yet.'
        : activeProfileCount > 0
        ? `${activeProfileCount} Team profile${activeProfileCount === 1 ? '' : 's'} added. Open Team to invite work emails, connect Workspace, and finish numbers per person.`
        : 'Add the owner or first teammate with their company work email. Workspace and phone-number setup happen from Team.',
      done: soloMembership ? soloGoogleConnected : activeProfileCount > 0,
      icon: Mail,
      to: soloMembership ? '/setup' : '/members',
      action: soloMembership
        ? soloGoogleConnected
          ? 'Connected'
          : googleAuthorizationStarting
            ? 'Starting Google…'
            : 'Authorize Gmail and Calendar'
        : activeProfileCount > 0 ? 'Open Team' : 'Add team member',
      buttonAction: soloMembership && !soloGoogleConnected ? startGoogleAuthorization : undefined,
      disabled: soloMembership ? !googleWorkspaceConfigured || soloGoogleConnected || googleAuthorizationStarting : false,
      muted: soloMembership ? soloGoogleConnected : false,
      completedTone: soloMembership && soloGoogleConnected,
      statusOnly: soloMembership && soloGoogleConnected,
    },
    {
      label: 'Connect your business line',
      detail: businessLineConnected
        ? `${businessLineCount} business line${businessLineCount === 1 ? '' : 's'} connected.`
        : soloMembership
          ? 'Add the phone number customers call.'
          : 'Add or assign the first company phone number.',
      done: businessLineConnected,
      icon: Phone,
      to: '/numbers',
      action: businessLineConnected ? 'Connected' : 'Connect line',
      disabled: !billingActive,
      completedTone: businessLineConnected,
      statusOnly: businessLineConnected,
    },
  ];
  const setupGuide = setupGuideContent({
    billingActive,
    engineInstalled,
    installerAcknowledged: effectiveInstallerAcknowledged,
    engineHandoffReady,
    businessSetupLoaded,
    engineRunning,
    engineStartLink,
    localAiReady,
    localAiStatusLabel: localAiSetup.statusLabel,
    openLocalAiSetupLink,
    activeProfileCount,
    soloMembership,
    soloGoogleConnected,
    googleWorkspaceConfigured,
    googleAuthorizationStarting,
    startGoogleAuthorization,
    businessLineConnected,
  });

  const launchDialogCopy = engineLaunchDialogCopy(engineLaunchDialog?.kind, localAiSetup.fallbackCommand);

  return (
    <>
      <AdminActionDialog
        open={Boolean(engineLaunchDialog)}
        eyebrow={launchDialogCopy.eyebrow}
        title={launchDialogCopy.title}
        description={launchDialogCopy.description}
        confirmLabel={launchDialogCopy.confirmLabel}
        cancelLabel="Stay here"
        details={launchDialogCopy.details}
        footerNote="Tip: if your browser shows a second system prompt, approve it so Oracord can continue setup."
        onCancel={() => setEngineLaunchDialog(null)}
        onConfirm={confirmEngineLaunch}
      />
      <PageHeader
        eyebrow="Setup"
        title="Finish setup"
        description="Follow the checklist once. Oracord will tell you exactly what needs attention."
      />
      <div className="grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
        <SectionCard className="p-5 sm:p-6">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Checklist</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">Get ready for your first call</h2>
            <p className="mt-1 text-sm text-slate-600">Plan, Engine, Google, and your business line. Green means that step is done.</p>
          </div>
          <div className="mb-5 grid gap-2 rounded-2xl border border-slate-100 bg-slate-50/70 p-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Local AI</div>
              <div className={`mt-1 text-sm font-bold ${localAiReady ? 'text-emerald-700' : 'text-slate-700'}`}>
                {localAiSetup.statusLabel}
              </div>
            </div>
            {visibleEngineStatusItems.map(item => (
              <div key={item.mark} className="rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.label}</div>
                <div className={`mt-1 text-sm font-bold ${item.done ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {item.done ? item.readyLabel : item.notReadyLabel}
                </div>
              </div>
            ))}
          </div>
          {engineSetupFlow.acknowledgementNote && (
            <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-blue-900">
              {engineSetupFlow.acknowledgementNote}
            </div>
          )}
          {showEngineInstallCard && (
            <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Step 1</div>
                  <h3 className="mt-1 text-sm font-bold text-emerald-950">
                    {installerAcknowledgedOnly ? 'Installation acknowledged locally' : 'Download Oracord Engine first'}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-800">
                    {installerAcknowledgedOnly ? engineSetupFlow.installDetail : `${engineDownload.detail} ${engineDownload.platformNote}`}
                  </p>
                </div>
                {engineDownload.path ? (
                  <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                    <a
                      href={engineDownload.path}
                      download
                      onClick={recordInstallerDownloaded}
                      className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-950 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                    >
                      {installerAcknowledgedOnly ? 'Download/reinstall' : engineDownload.action}
                    </a>
                    {installerAcknowledgedOnly ? (
                      <a
                        href={pairingLaunchLink}
                        onClick={engineSetupFlow.pairDisabled ? undefined : openPairingSetupLink}
                        className={`inline-flex items-center justify-center rounded-xl border border-emerald-200 px-3 py-2 text-sm font-semibold shadow-sm transition ${engineSetupFlow.pairDisabled ? 'pointer-events-none bg-emerald-50 text-emerald-400' : 'bg-emerald-900 text-white hover:bg-emerald-800'}`}
                      >
                        {engineSetupFlow.pairAction}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={acknowledgeInstaller}
                        className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
                      >
                        I’ve installed it
                      </button>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/settings"
                    className="inline-flex shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-950 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    Open install help
                  </Link>
                )}
              </div>
            </div>
          )}
          {showLocalAiSetupCard && (
            <div className="mb-5 rounded-2xl border border-violet-100 bg-violet-50/70 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-700">Local AI</div>
                  <h3 className="mt-1 text-sm font-bold text-violet-950">Set up local AI on this Mac</h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-violet-900">{localAiSetup.detail}</p>
                  <div className="mt-3 rounded-xl border border-violet-200 bg-white/80 p-3 text-sm leading-6 text-violet-950">
                    <div className="font-semibold">What to expect</div>
                    <div className="mt-1 text-xs text-violet-800">Oracord Engine opens on this Mac, guides any Mac app/helper permissions, and reports progress back here. No Terminal or Homebrew is part of the normal setup path.</div>
                  </div>
                  {localAiSetupLaunched && (
                    <div className="mt-3 flex gap-3 rounded-xl border border-violet-200 bg-white/90 p-3 text-sm leading-6 text-violet-950">
                      <div className="mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-violet-200 border-t-violet-700" aria-hidden="true" />
                      <div>
                        <div className="font-semibold">Downloading the local model — this can take 3–5 minutes.</div>
                        <div className="mt-1 text-xs text-violet-800">You can keep going. This page checks Engine automatically and marks Local AI ready when the check passes.</div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 rounded-xl border border-violet-200 bg-white/80 p-3 text-sm leading-6 text-violet-950">
                    <div className="font-semibold">Manual recovery only</div>
                    <div className="text-xs text-violet-800">You should not need Terminal. Use this only if support asks for it:</div>
                    <code className="mt-1 block overflow-x-auto rounded bg-violet-100 px-2 py-1 text-xs text-violet-950">{localAiSetup.fallbackCommand}</code>
                  </div>
                  {localAiSetupLaunched && (
                    <p className="mt-2 text-xs font-semibold text-violet-800">Local LLM setup launched from /setup. You can continue below while Oracord checks for completion.</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={openLocalAiSetupLink}
                    className="inline-flex items-center justify-center rounded-xl border border-violet-200 bg-violet-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-800"
                  >
                    {localAiSetup.action}
                  </button>
                  <button
                    type="button"
                    onClick={checkStatus}
                    className="inline-flex items-center justify-center rounded-xl border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-950 shadow-sm transition hover:border-violet-300 hover:bg-violet-50"
                  >
                    Check status
                  </button>
                </div>
              </div>
            </div>
          )}
          {showEngineRepairCard && (
            <div className="mb-5 rounded-2xl border border-amber-100 bg-amber-50/60 p-4 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Engine help</div>
                  <h3 className="mt-1 text-sm font-bold text-amber-950">Only use these if Engine gets stuck</h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-amber-900">
                    Your normal next step is still the checklist. These options are here for Mac prompts, failed pairing, or a service that will not start.
                  </p>
                </div>
                <div className="grid shrink-0 gap-2 sm:grid-cols-3 lg:min-w-[28rem]">
                  {engineDownload.path ? (
                    <a
                      href={engineDownload.path}
                      download
                      onClick={recordInstallerDownloaded}
                      className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-amber-950 shadow-sm transition hover:border-amber-300 hover:bg-amber-50"
                    >
                      Reinstall Engine
                      <span className="mt-1 block text-xs font-normal leading-5 text-amber-800">Replaces the app. Keeps setup.</span>
                    </a>
                  ) : null}
                  <a
                    href={pairingLaunchLink}
                    onClick={openPairingSetupLink}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${billingActive && activeOrganizationId ? 'border-amber-200 bg-white text-amber-950 hover:border-amber-300 hover:bg-amber-50' : 'pointer-events-none border-amber-100 bg-amber-50 text-amber-300'}`}
                  >
                    Pair again
                    <span className="mt-1 block text-xs font-normal leading-5 text-amber-800">Reconnects this Mac.</span>
                  </a>
                  <Link
                    to="/settings?tab=oracord"
                    className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-amber-950 shadow-sm transition hover:border-amber-300 hover:bg-amber-50"
                  >
                    More help
                    <span className="mt-1 block text-xs font-normal leading-5 text-amber-800">Advanced repair details.</span>
                  </Link>
                </div>
              </div>
            </div>
          )}
          {showGoogleAccountMismatchNote && (
            <div className={`mb-4 rounded-2xl border p-4 text-sm leading-6 ${signedInWithPersonalGoogle ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-blue-100 bg-blue-50 text-blue-900'}`}>
              <p className="font-semibold">Signed in as {signedInEmail}</p>
              <p className="mt-1">
                {signedInWithPersonalGoogle
                  ? 'This is a personal Google account. You can keep it as the admin login, but Oracord cannot connect company Gmail or Calendar from it. Add your company Workspace email as a Team member, then connect Workspace from that Team profile.'
                  : 'To connect Workspace, add the company work email as a Team member and use that Team profile for Google authorization.'}
              </p>
            </div>
          )}
          {googleAuthorizationError && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              {googleAuthorizationError}
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {steps.map(step => {
              const Icon = step.icon;
              const StatusIcon = step.done ? CheckCircle2 : Circle;
              const actionClass = 'completedTone' in step && step.completedTone
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50';
              return (
                <div key={step.label} className={`flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between ${'muted' in step && step.muted ? 'opacity-60' : ''}`}>
                  <div className="flex gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-slate-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusIcon className={`h-4 w-4 ${step.done ? 'text-emerald-600' : 'text-slate-300'}`} />
                        <h3 className="text-sm font-bold text-slate-950">{step.label}</h3>
                        {step.sourceStatus && <SourceStatusPill status={step.sourceStatus} />}
                      </div>
                      <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">{step.detail}</p>
                      {'sourceStatus' in step && openedSetupLink && step.sourceStatus && <p className="mt-1 text-xs leading-5 text-blue-800">{step.sourceStatus.detail}</p>}
                    </div>
                  </div>
                  {'statusOnly' in step && step.statusOnly ? (
                    <span className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm ${actionClass}`}>
                      {step.action}
                    </span>
                  ) : 'buttonAction' in step && step.buttonAction ? (
                    <button
                      type="button"
                      disabled={'disabled' in step && step.disabled}
                      onClick={step.buttonAction}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${actionClass}`}
                    >
                      {step.action}
                    </button>
                  ) : 'disabled' in step && step.disabled ? (
                    <span className="inline-flex shrink-0 cursor-not-allowed items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-500 shadow-sm">
                      {step.action}
                    </span>
                  ) : step.externalDownload || step.externalAction ? (
                    <a
                      href={step.to}
                      download={step.externalDownload || undefined}
                      onClick={step.onClick}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${actionClass}`}
                    >
                      {step.action}
                    </a>
                  ) : (
                    <Link
                      to={step.to}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${actionClass}`}
                    >
                      {step.action}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
        <SectionCard className="p-5 sm:p-6">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Now</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">{setupGuide.title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">{setupGuide.description}</p>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4 text-sm leading-6 text-slate-600">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">What this means</div>
            <p className="mt-2">{setupGuide.why}</p>
          </div>
          <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-blue-900">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-500">Next</div>
            <p className="mt-2">{setupGuide.next}</p>
          </div>
          {showGoogleAccountMismatchNote && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              If this admin login is personal Gmail, add the company Workspace email as a Team member before connecting Gmail and Calendar.
            </div>
          )}
          {'buttonAction' in setupGuide && setupGuide.buttonAction ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={setupGuide.buttonAction}
                disabled={setupGuide.disabled}
                className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {setupGuide.action}
              </button>
            </div>
          ) : setupGuide.to && (
            <div className="mt-4 flex flex-wrap gap-2">
              {'externalAction' in setupGuide && setupGuide.externalAction ? (
                <a
                  href={setupGuide.to}
                  onClick={openEngineStartLink}
                  className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                >
                  {setupGuide.action}
                </a>
              ) : (
                <Link to={setupGuide.to} className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50">
                  {setupGuide.action}
                </Link>
              )}
            </div>
          )}
        </SectionCard>
      </div>
    </>
  );
}


function emailDomain(email: string | null): string | null {
  const at = email?.lastIndexOf('@') ?? -1;
  return at >= 0 ? email!.slice(at + 1).trim().toLowerCase() : null;
}

function engineLaunchDialogCopy(kind: 'pair' | 'start' | 'local-ai' | undefined, localAiFallbackCommand: string) {
  if (kind === 'pair') {
    return {
      eyebrow: 'Engine setup',
      title: 'Open Oracord Engine?',
      description: 'Oracord will hand this company’s setup package to the Engine app installed on this Mac. Your browser may ask for permission one more time before Engine opens.',
      confirmLabel: 'Open Oracord Engine',
      details: [
        'This pairs the local Engine with the current company account.',
        'No setup code needs to be copied or pasted.',
        'After Engine reports back, this checklist will update automatically.',
      ],
    };
  }
  if (kind === 'local-ai') {
    return {
      eyebrow: 'Local LLM setup',
      title: 'Set up local LLM?',
      description: 'Oracord will open the local Engine app to guide local LLM setup on this Mac. You stay on Setup while status updates.',
      confirmLabel: 'Set up local LLM',
      details: [
        'Primary path: the Engine helper opens from this Setup page.',
        'No Terminal or Homebrew is needed for normal onboarding.',
        `Manual recovery only: ${localAiFallbackCommand}`,
      ],
    };
  }
  return {
    eyebrow: 'Engine service',
    title: 'Start Oracord Engine?',
    description: 'Oracord will ask the local Engine app to start its background service so it can keep reporting setup status for this company.',
    confirmLabel: 'Start Engine',
    details: [
      'Engine runs locally on this Mac.',
      'The background service keeps Oracord connected after setup.',
      'If macOS asks for approval, choose Allow or Open.',
    ],
  };
}

function SourceStatusPill({ status }: { status: SetupSourceStatus }) {
  const classes = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-800 ring-amber-100',
    blue: 'bg-blue-50 text-blue-800 ring-blue-100',
    slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  } satisfies Record<SetupSourceStatus['tone'], string>;

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ring-1 ${classes[status.tone]}`}>
      {status.label}
    </span>
  );
}

function buildOracordEnginePairingLaunchLink(organizationId: string) {
  const params = new URLSearchParams({ organizationId });
  return `${API_BASE_URL}/api/admin/settings/oracord-engine/pairing-launch?${params.toString()}`;
}

function formatPlan(plan: string) {
  return plan
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}

function isSoloPlan(plan: string | null) {
  return Boolean(plan && /(^|[-_\s])solo($|[-_\s])/.test(plan.toLowerCase()));
}

function installerStorageKey(scope: string, key: 'downloaded' | 'installed-acknowledged') {
  return `oracord-engine:${scope}:${key}`;
}

function setupGuideContent({
  billingActive,
  engineInstalled,
  installerAcknowledged,
  engineHandoffReady,
  businessSetupLoaded,
  engineRunning,
  engineStartLink,
  localAiReady,
  localAiStatusLabel,
  openLocalAiSetupLink,
  activeProfileCount,
  soloMembership,
  soloGoogleConnected,
  googleWorkspaceConfigured,
  googleAuthorizationStarting,
  startGoogleAuthorization,
  businessLineConnected,
}: {
  billingActive: boolean;
  engineInstalled: boolean;
  installerAcknowledged: boolean;
  engineHandoffReady: boolean;
  businessSetupLoaded: boolean;
  engineRunning: boolean;
  engineStartLink: string;
  localAiReady: boolean;
  localAiStatusLabel: string;
  openLocalAiSetupLink: () => void;
  activeProfileCount: number;
  soloMembership: boolean;
  soloGoogleConnected: boolean;
  googleWorkspaceConfigured: boolean;
  googleAuthorizationStarting: boolean;
  startGoogleAuthorization: () => void;
  businessLineConnected: boolean;
}) {
  if (!billingActive) {
    return {
      title: 'Choose a plan',
      description: 'Start with Solo for one owner, or Team when multiple people need access.',
      why: 'Your plan sets how many people and business lines are included.',
      next: 'Pick Solo or Team. You can add more lines later.',
      to: '/billing',
      action: 'Choose plan',
    };
  }
  if (!engineInstalled && !installerAcknowledged) {
    return {
      title: 'Install Engine on this Mac',
      description: 'Engine is the small Mac app that lets Oracord work locally.',
      why: 'It connects this Mac to your company account and keeps setup private to your machine.',
      next: 'Download the installer, open it, approve the Mac prompts, then click “I’ve installed it.”',
      to: null,
      action: null,
    };
  }
  if (!engineInstalled && installerAcknowledged) {
    return {
      title: 'Pair this Mac',
      description: 'Pairing tells Engine which company account it belongs to.',
      why: 'After pairing, this page can update automatically when Engine is ready.',
      next: 'Click Pair this Mac once, then approve the Mac prompt if it appears.',
      to: null,
      action: null,
    };
  }
  if (!engineHandoffReady) {
    return {
      title: 'Pair this Mac',
      description: 'Pairing connects Engine to this company account. No codes to copy.',
      why: 'Engine needs to know where to send setup status and future call work.',
      next: 'Click Pair this Mac once, then approve the Mac prompt if it appears.',
      to: null,
      action: null,
    };
  }
  if (!engineRunning) {
    return {
      title: 'Start Engine',
      description: 'Engine is installed and paired. Now start the local background service.',
      why: businessSetupLoaded
        ? 'The service keeps Engine available after setup, even if this browser tab is closed.'
        : 'The service keeps Engine available after setup. Company details load quietly in the background.',
      next: 'Click Start Engine. If macOS asks, choose Allow or Open.',
      to: engineStartLink,
      action: 'Start Engine',
      externalAction: true,
    };
  }
  if (!localAiReady) {
    return {
      title: 'Set up local AI',
      description: 'Engine is running. Next, set up the local model on this Mac.',
      why: 'Engine handles the download and checks it for you. You should not need Terminal or Homebrew.',
      next: `Click Set up local AI and stay on this page. Current status: ${localAiStatusLabel}.`,
      to: null,
      action: 'Set up local AI',
      buttonAction: openLocalAiSetupLink,
      disabled: false,
    };
  }
  if (soloMembership) {
    if (soloGoogleConnected) {
      if (!businessLineConnected) {
        return {
          title: 'Connect your business line',
          description: 'Google is connected. Now connect the phone number customers call.',
          why: 'Oracord uses the business line to attach calls, recordings, transcripts, and history to the right account.',
          next: 'Open Business Lines and add the first phone number.',
          to: '/numbers',
          action: 'Connect line',
        };
      }
      return {
        title: 'Ready for testing',
        description: 'Engine, Google, and your business line are connected.',
        why: 'Oracord has the basics it needs to start receiving calls and work history.',
        next: 'You can leave Setup here. Open Business Lines only if you want to check the phone number.',
        to: '/numbers',
        action: 'View business line',
      };
    }
    return {
      title: 'Connect Gmail and Calendar',
      description: googleWorkspaceConfigured
        ? 'For Solo, connect the Google account you want Oracord to use.'
        : 'Google connection is not ready yet. This needs to be fixed before Gmail and Calendar can connect.',
      why: 'Gmail and Calendar help Oracord understand customer follow-up and appointments.',
      next: googleWorkspaceConfigured
        ? 'Authorize Gmail and Calendar with the Google account you want Oracord to use.'
        : 'Set up Google connection, then return here.',
      to: null,
      action: googleWorkspaceConfigured
        ? googleAuthorizationStarting
          ? 'Starting Google…'
          : 'Authorize Gmail and Calendar'
        : null,
      buttonAction: googleWorkspaceConfigured ? startGoogleAuthorization : undefined,
      disabled: googleAuthorizationStarting,
    };
  }
  if (activeProfileCount === 0) {
    return {
      title: 'Add the first team profile',
      description: 'Now that the local setup is ready, add the owner or first teammate with their company work email.',
      why: 'Team profiles are where Workspace connection and business-line assignment happen for each person.',
      next: 'Add the first profile from Team. If the admin login is personal Gmail, use the company Workspace email for the Team profile.',
      to: '/members',
      action: 'Open Team',
    };
  }
  return {
    title: 'Setup is ready for the next teammate',
    description: 'The core setup is in place. Add more staff only when you are ready to assign Workspace context or business lines.',
    why: 'Keeping Team setup separate avoids clutter during the first install while still giving you a clear path when staffing is the next real job.',
    next: 'Continue in Team when you need to add another staff member or assign another business line.',
    to: '/members',
    action: 'Open Team',
  };
}
